<template>
  <section class="trek-loader" role="status" aria-live="polite" aria-label="Đang chuẩn bị hành trình">
    <div class="trek-loader__aurora trek-loader__aurora--one"></div>
    <div class="trek-loader__aurora trek-loader__aurora--two"></div>
    <div class="trek-loader__grain"></div>

    <div class="trek-loader__frame">
      <div class="trek-loader__copy">
        <p class="trek-loader__eyebrow">dichungxe</p>
        <h1 class="trek-loader__title">Trek together.<br>Live together.</h1>
        <p class="trek-loader__subtitle">Đang chuẩn bị hành trình...</p>

        <!-- LED-style dot progress bar -->
        <div class="trek-loader__led-progress" aria-hidden="true">
          <span v-for="n in 12" :key="n" class="led-dot" :style="{ '--i': n - 1 }"></span>
        </div>
      </div>

      <div class="trek-loader__scene" aria-hidden="true">
        <svg class="scene" viewBox="0 0 960 560" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="tl-skyGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stop-color="#061124" />
              <stop offset="0.45" stop-color="#0c2741" />
              <stop offset="1" stop-color="#164557" />
            </linearGradient>

            <linearGradient id="tl-roadGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stop-color="#1a2a3a" />
              <stop offset="1" stop-color="#07101d" />
            </linearGradient>

            <linearGradient id="tl-busGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stop-color="#f0e8d4" />
              <stop offset="0.5" stop-color="#e8c870" />
              <stop offset="1" stop-color="#b8722e" />
            </linearGradient>

            <linearGradient id="tl-busStripeGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stop-color="#1a5f8a" />
              <stop offset="0.5" stop-color="#2d9fcf" />
              <stop offset="1" stop-color="#1a5f8a" />
            </linearGradient>

            <linearGradient id="tl-winGrad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stop-color="#c8f0ff" />
              <stop offset="0.6" stop-color="#5ab4d8" />
              <stop offset="1" stop-color="#1a5878" />
            </linearGradient>

            <linearGradient id="tl-hlGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stop-color="#fff7c8" stop-opacity="0.9" />
              <stop offset="0.5" stop-color="#ffd37a" stop-opacity="0.35" />
              <stop offset="1" stop-color="#ffd37a" stop-opacity="0" />
            </linearGradient>

            <radialGradient id="tl-moonGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0" stop-color="#fff7db" stop-opacity="0.95" />
              <stop offset="0.65" stop-color="#b9e8ef" stop-opacity="0.2" />
              <stop offset="1" stop-color="#b9e8ef" stop-opacity="0" />
            </radialGradient>

            <!-- Flat road clip for dash rects -->
            <clipPath id="tl-roadClip">
              <rect x="0" y="445" width="960" height="115" />
            </clipPath>

            <linearGradient id="tl-neonGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stop-color="#22c55e" />
              <stop offset="0.55" stop-color="#4ade80" />
              <stop offset="1" stop-color="#86efac" />
            </linearGradient>

            <filter id="tl-glow" x="-40%" y="-40%" width="180%" height="180%">
              <feGaussianBlur stdDeviation="10" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>

            <filter id="tl-puffBlur" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="4" />
            </filter>

            <filter id="tl-neonGlow" x="-50%" y="-80%" width="200%" height="260%">
              <feGaussianBlur stdDeviation="1.2" result="tube" />
              <feGaussianBlur in="SourceGraphic" stdDeviation="4" result="halo" />
              <feGaussianBlur in="SourceGraphic" stdDeviation="10" result="bloom" />
              <feMerge>
                <feMergeNode in="bloom" />
                <feMergeNode in="halo" />
                <feMergeNode in="tube" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          <!-- Sky -->
          <rect width="960" height="560" rx="42" fill="url(#tl-skyGrad)" />

          <!-- Moon halo -->
          <circle class="tl-moon" cx="760" cy="110" r="72" fill="url(#tl-moonGlow)" />
          <circle cx="760" cy="110" r="28" fill="#fff4d1" opacity="0.95" />

          <!-- Stars -->
          <g class="tl-stars">
            <circle cx="102" cy="82" r="2.4" />
            <circle cx="164" cy="146" r="1.5" />
            <circle cx="244" cy="86" r="1.8" />
            <circle cx="342" cy="122" r="1.3" />
            <circle cx="516" cy="76" r="1.9" />
            <circle cx="612" cy="154" r="1.4" />
            <circle cx="842" cy="184" r="1.7" />
            <circle cx="78"  cy="178" r="1.2" />
            <circle cx="448" cy="52"  r="2.0" />
            <circle cx="692" cy="68"  r="1.6" />
          </g>

          <!-- Mountains -->
          <g class="tl-mtn tl-mtn--far">
            <path d="M0 338 L86 240 L142 286 L238 164 L330 286 L430 190 L526 296 L622 178 L736 292 L820 216 L960 318 L960 560 L0 560 Z" />
          </g>
          <g class="tl-mtn tl-mtn--mid">
            <path d="M0 384 L72 300 L132 328 L226 218 L316 330 L408 236 L512 354 L602 252 L710 352 L798 278 L900 358 L960 326 L960 560 L0 560 Z" />
          </g>
          <g class="tl-mtn tl-mtn--near">
            <path d="M0 430 L62 350 L122 384 L196 300 L280 388 L352 318 L446 410 L528 332 L628 424 L720 346 L816 420 L900 364 L960 400 L960 560 L0 560 Z" />
          </g>

          <!-- Fog -->
          <g class="tl-fog tl-fog--back">
            <path d="M-80 338 C80 300 190 370 340 330 C486 292 598 344 746 318 C872 296 1008 326 1080 296 L1080 378 L-80 386 Z" />
          </g>
          <g class="tl-fog tl-fog--front">
            <path d="M-140 434 C30 388 156 464 316 422 C484 378 598 456 760 416 C888 386 1018 420 1120 382 L1120 486 L-140 494 Z" />
          </g>

          <!-- Road flat surface -->
          <rect class="tl-road-shadow" x="0" y="456" width="960" height="8" />
          <rect class="tl-road" x="0" y="462" width="960" height="98" />

          <!-- Lane dashes scrolling left — 9 rects + 1 offscreen for seamless wrap -->
          <g class="tl-road-lines" clip-path="url(#tl-roadClip)">
            <rect x="0"    y="500" width="80" height="8" rx="4" />
            <rect x="140"  y="500" width="80" height="8" rx="4" />
            <rect x="280"  y="500" width="80" height="8" rx="4" />
            <rect x="420"  y="500" width="80" height="8" rx="4" />
            <rect x="560"  y="500" width="80" height="8" rx="4" />
            <rect x="700"  y="500" width="80" height="8" rx="4" />
            <rect x="840"  y="500" width="80" height="8" rx="4" />
            <rect x="980"  y="500" width="80" height="8" rx="4" />
            <rect x="1120" y="500" width="80" height="8" rx="4" />
          </g>

          <!-- Headlight beam (behind coach) -->
          <g class="tl-headlight" filter="url(#tl-glow)">
            <path d="M596 390 L900 318 L910 396 L608 416 Z" fill="url(#tl-hlGrad)" />
          </g>

          <!-- Exhaust puffs (behind bus body) -->
          <circle class="tl-puff tl-puff--3" cx="112" cy="422" r="18" filter="url(#tl-puffBlur)" />
          <circle class="tl-puff tl-puff--2" cx="142" cy="432" r="13" filter="url(#tl-puffBlur)" />
          <circle class="tl-puff tl-puff--1" cx="165" cy="440" r="9"  filter="url(#tl-puffBlur)" />

          <!-- Coach body (suspension bounce applied here) -->
          <g class="tl-coach">
            <!-- Ground shadow -->
            <ellipse class="tl-coach-shadow" cx="405" cy="464" rx="205" ry="14" />

            <!-- Main body shell -->
            <path class="tl-body-main" d="
              M190 326
              C190 302 208 282 232 282
              H530
              C574 282 608 314 616 356
              L624 402
              C628 424 610 446 586 446
              H220
              C196 446 178 426 182 402 Z" />

            <!-- Lower skirt -->
            <path class="tl-body-lower" d="M188 396 H622 L612 430 C608 440 596 447 582 447 H226 C210 447 194 436 190 420 Z" />

            <!-- Blue accent stripe -->
            <line class="tl-stripe" x1="188" y1="364" x2="620" y2="364" />

            <!-- Neon roof sign: leaf green + sky blue glass tube -->
            <g class="tl-neon">
              <line class="tl-neon-arm" x1="190" y1="296" x2="190" y2="252" />
              <line class="tl-neon-arm" x1="612" y1="296" x2="612" y2="252" />
              <path class="tl-neon-backrail" d="M170 258 H632" />

              <g class="tl-neon-icon" filter="url(#tl-neonGlow)">
                <path class="tl-neon-route" d="M188 229 C208 208 237 208 258 228 S302 248 328 219" />
                <circle class="tl-neon-pin tl-neon-pin--one" cx="188" cy="229" r="4" />
                <circle class="tl-neon-pin tl-neon-pin--two" cx="328" cy="219" r="4" />
                <path class="tl-neon-leaf" d="M286 204 C314 175 361 184 370 224 C337 235 302 229 286 204 Z" />
                <path class="tl-neon-leaf-vein" d="M295 207 C316 205 344 211 365 223" />
              </g>

              <text class="tl-neon-halo" x="426" y="245" text-anchor="middle">dichungxe</text>
              <text class="tl-neon-text" x="426" y="245" text-anchor="middle" filter="url(#tl-neonGlow)">dichungxe</text>
            </g>

            <!-- Windshield -->
            <path class="tl-front-glass" d="M530 298 C566 302 594 328 602 362 L534 362 Z" />

            <!-- Passenger windows -->
            <rect class="tl-window" x="232" y="298" width="56" height="44" rx="8" />
            <rect class="tl-window" x="302" y="298" width="56" height="44" rx="8" />
            <rect class="tl-window" x="372" y="298" width="56" height="44" rx="8" />
            <rect class="tl-window" x="442" y="298" width="56" height="44" rx="8" />

            <!-- Tail lamp bar -->
            <rect class="tl-tail-lamp" x="179" y="360" width="14" height="44" rx="3" />
            <rect class="tl-tail-lamp-top" x="182" y="338" width="10" height="18" rx="2" />

            <!-- Front headlamp -->
            <ellipse class="tl-lamp--front" cx="608" cy="396" rx="11" ry="9" />
            <!-- DRL strip -->
            <rect class="tl-drl" x="601" y="377" width="14" height="4" rx="2" />

            <!-- Exhaust pipe nozzle -->
            <rect class="tl-exhaust-pipe" x="172" y="420" width="24" height="10" rx="5" />
          </g>

          <!-- Wheels rendered outside tl-coach so bounce doesn't affect their spin center -->
          <g class="tl-wheel tl-wheel--rear">
            <circle class="tl-wheel-tire" cx="282" cy="447" r="34" />
            <circle class="tl-wheel-hub"  cx="282" cy="447" r="22" />
            <circle class="tl-wheel-cap"  cx="282" cy="447" r="9" />
            <line class="tl-spoke" x1="282" y1="425" x2="282" y2="469" />
            <line class="tl-spoke" x1="260" y1="447" x2="304" y2="447" />
            <line class="tl-spoke" x1="266" y1="431" x2="298" y2="463" />
            <line class="tl-spoke" x1="298" y1="431" x2="266" y2="463" />
          </g>
          <g class="tl-wheel tl-wheel--front">
            <circle class="tl-wheel-tire" cx="520" cy="447" r="34" />
            <circle class="tl-wheel-hub"  cx="520" cy="447" r="22" />
            <circle class="tl-wheel-cap"  cx="520" cy="447" r="9" />
            <line class="tl-spoke" x1="520" y1="425" x2="520" y2="469" />
            <line class="tl-spoke" x1="498" y1="447" x2="542" y2="447" />
            <line class="tl-spoke" x1="504" y1="431" x2="536" y2="463" />
            <line class="tl-spoke" x1="536" y1="431" x2="504" y2="463" />
          </g>
        </svg>
      </div>
    </div>

    <span class="sr-only">Đang chuẩn bị hành trình...</span>
  </section>
</template>

<script>
export default {
  name: 'BusLoadingAnimation'
}
</script>

<!-- Scoped: layout, overlay, copy, LED dot progress -->
<style scoped lang="scss">
.trek-loader {
  --ink:       #f7f1df;
  --muted:     rgba(247, 241, 223, 0.72);
  --gold:      #f0c777;
  --teal:      #58d2d2;
  --led-green: #22c55e;
  --led-blue:  #4ade80;

  position: fixed;
  inset: 0;
  z-index: 9999;
  display: grid;
  place-items: center;
  min-height: 100vh;
  overflow: hidden;
  color: var(--ink);
  background:
    radial-gradient(circle at 18% 16%, rgba(88,210,210,0.18), transparent 30%),
    radial-gradient(circle at 82% 24%, rgba(240,199,119,0.16), transparent 24%),
    linear-gradient(135deg, #040a16 0%, #07152a 44%, #0b2b38 100%);
  isolation: isolate;
  animation: tl-enter 420ms ease-out both;
}

.trek-loader__aurora {
  position: absolute;
  width: 52vw; height: 52vw;
  border-radius: 999px;
  filter: blur(70px);
  opacity: 0.42;
  pointer-events: none;
  will-change: transform;
  transform: translateZ(0);
}
@supports (mix-blend-mode: screen) {
  .trek-loader__aurora { mix-blend-mode: screen; }
}
.trek-loader__aurora--one {
  top: -24vw; left: -10vw;
  background: rgba(88,210,210,0.42);
  animation: tl-aurora1 8s ease-in-out infinite alternate;
}
.trek-loader__aurora--two {
  right: -20vw; bottom: -22vw;
  background: rgba(240,199,119,0.32);
  animation: tl-aurora2 10s ease-in-out infinite alternate;
}

.trek-loader__grain {
  position: absolute;
  inset: 0;
  opacity: 0.18;
  pointer-events: none;
  background-image:
    linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.018) 1px, transparent 1px);
  background-size: 36px 36px;
  mask-image: radial-gradient(circle at center, black 0%, transparent 78%);
}

.trek-loader__frame {
  position: relative;
  z-index: 1;
  width: min(1120px, calc(100vw - 32px));
  min-height: min(680px, calc(100vh - 32px));
  display: grid;
  grid-template-columns: 0.72fr 1.28fr;
  align-items: center;
  gap: clamp(18px, 4vw, 54px);
  padding: clamp(24px, 5vw, 58px);
  border: 1px solid rgba(247,241,223,0.16);
  border-radius: clamp(28px, 4vw, 52px);
  background: rgba(5,12,24,0.78);
  box-shadow:
    0 42px 120px rgba(0,0,0,0.46),
    inset 0 1px 0 rgba(255,255,255,0.18);
}
@supports (backdrop-filter: blur(18px)) or (-webkit-backdrop-filter: blur(18px)) {
  .trek-loader__frame {
    background:
      linear-gradient(135deg, rgba(255,255,255,0.11), rgba(255,255,255,0.04)),
      rgba(5,12,24,0.48);
    backdrop-filter: blur(18px);
    -webkit-backdrop-filter: blur(18px);
  }
}

.trek-loader__copy {
  align-self: stretch;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
}

.trek-loader__eyebrow {
  margin: 0 0 18px;
  font-size: clamp(12px, 1.4vw, 15px);
  font-weight: 800;
  letter-spacing: 0.34em;
  text-transform: uppercase;
  color: var(--gold);
}

.trek-loader__title {
  margin: 0;
  max-width: 9ch;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: clamp(44px, 6.2vw, 86px);
  line-height: 0.9;
  letter-spacing: -0.075em;
  font-weight: 700;
  text-wrap: balance;
  text-shadow: 0 18px 50px rgba(0,0,0,0.45);
}

.trek-loader__subtitle {
  width: fit-content;
  margin: 24px 0 0;
  padding: 10px 18px;
  color: var(--muted);
  font-size: clamp(13px, 1.4vw, 16px);
  font-weight: 700;
  letter-spacing: 0.02em;
  border: 1px solid rgba(240,199,119,0.24);
  border-radius: 999px;
  background: rgba(6,17,36,0.48);
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.12);
  animation: tl-pulse 2.2s ease-in-out infinite;
}

/* LED dot progress */
.trek-loader__led-progress {
  display: flex;
  gap: 7px;
  margin-top: 28px;
}
.led-dot {
  display: block;
  width: 10px;
  height: 14px;
  border-radius: 2px;
  background: var(--led-green);
  box-shadow: 0 0 8px var(--led-green), 0 0 16px rgba(57,255,130,0.4);
  animation: tl-led-dot 1.4s ease-in-out infinite;
  animation-delay: calc(var(--i) * 0.12s);
  opacity: 0.22;
}

.trek-loader__scene {
  min-width: 0;
  filter: drop-shadow(0 36px 70px rgba(0,0,0,0.42));
}
.scene {
  display: block;
  width: 100%;
  height: auto;
  border-radius: 42px;
  overflow: hidden;
}

.sr-only {
  position: absolute;
  width: 1px; height: 1px;
  padding: 0; margin: -1px;
  overflow: hidden;
  clip: rect(0,0,0,0);
  white-space: nowrap;
  border: 0;
}


/* Responsive */
@media (max-width: 900px) {
  .trek-loader__frame {
    grid-template-columns: 1fr;
    align-content: center;
    gap: 22px;
  }
  .trek-loader__copy { text-align: center; align-items: center; }
  .trek-loader__title { max-width: 12ch; }
  .trek-loader__scene { width: min(720px, 100%); margin: 0 auto; }
}
@media (max-width: 560px) {
  .trek-loader { padding: 12px; }
  .trek-loader__frame {
    width: 100%;
    min-height: calc(100vh - 24px);
    padding: 22px;
    border-radius: 30px;
  }
  .trek-loader__eyebrow { margin-bottom: 12px; letter-spacing: 0.28em; }
  .trek-loader__title { font-size: clamp(38px, 15vw, 62px); }
  .trek-loader__subtitle { margin-top: 18px; padding: 10px 14px; }
  .scene { border-radius: 28px; }
}
</style>

<!-- Unscoped: SVG element animations (scoped attrs can't reach inside SVG) -->
<style lang="scss">
/* Stars */
.tl-stars circle {
  fill: #fff7d6; opacity: 0.72;
  animation: tl-star 2.8s ease-in-out infinite;
}
.tl-stars circle:nth-child(2n) { animation-delay: 0.8s; }
.tl-stars circle:nth-child(3n) { animation-delay: 1.5s; }
.tl-stars circle:nth-child(5n) { animation-delay: 0.4s; }

.tl-moon { animation: tl-moon 5s ease-in-out infinite; }

/* Mountains */
.tl-mtn path { transform-origin: 480px 560px; }
.tl-mtn--far  path { fill: #1a4b5f; opacity: 0.62; animation: tl-mtn-far  12s ease-in-out infinite alternate; }
.tl-mtn--mid  path { fill: #12394b; opacity: 0.82; animation: tl-mtn-mid  10s ease-in-out infinite alternate; }
.tl-mtn--near path { fill: #0a2433; opacity: 0.96; animation: tl-mtn-near  8s ease-in-out infinite alternate; }

/* Fog */
.tl-fog path { fill: rgba(210,241,239,0.24); }
.tl-fog--back  path { animation: tl-fog-back  11s linear infinite; }
.tl-fog--front path { fill: rgba(238,247,240,0.32); animation: tl-fog-front 7s linear infinite; }

/* Road */
.tl-road        { fill: #10202e; }
.tl-road-shadow { fill: rgba(0,0,0,0.6); }

/* Lane dashes scroll left; seamless because total span 1200px, offset 140px */
.tl-road-lines {
  animation: tl-road-flow 1.0s linear infinite;
  will-change: transform;
}
.tl-road-lines rect { fill: rgba(247,241,223,0.52); }

/* Headlight beam */
.tl-headlight { animation: tl-hl-breathe 2s ease-in-out infinite; }

/* Coach — vertical suspension bounce only */
.tl-coach {
  animation: tl-coach-bounce 1.1s ease-in-out infinite;
  transform-origin: 405px 445px;
  will-change: transform;
}
.tl-coach-shadow {
  fill: rgba(0,0,0,0.42);
  animation: tl-shadow 1.1s ease-in-out infinite;
  transform-origin: 405px 464px;
}

/* Body */
.tl-body-main {
  fill: url(#tl-busGrad);
  stroke: rgba(255,247,216,0.55);
  stroke-width: 2.5;
}
.tl-body-lower { fill: rgba(80,50,25,0.38); }

/* Blue stripe */
.tl-stripe {
  stroke: url(#tl-busStripeGrad);
  stroke-width: 10;
  stroke-linecap: round;
}

/* Neon roof sign */
.tl-neon-arm {
  stroke: rgba(255, 255, 255, 0.34);
  stroke-width: 2.4;
  stroke-linecap: round;
}
.tl-neon-backrail {
  fill: none;
  stroke: rgba(22, 76, 48, 0.8);
  stroke-width: 8;
  stroke-linecap: round;
  filter: drop-shadow(0 3px 7px rgba(0,0,0,0.42));
}
.tl-neon-text,
.tl-neon-halo {
  font-family: 'Lobster', 'Pacifico', 'Brush Script MT', 'Lucida Handwriting', cursive;
  font-size: 74px;
  font-weight: 400;
  letter-spacing: -0.02em;
}
.tl-neon-text {
  fill: #eaffff;
  stroke: url(#tl-neonGrad);
  stroke-width: 2.1;
  paint-order: stroke fill;
  animation: tl-neon-flicker 5.5s infinite;
}
.tl-neon-halo {
  fill: none;
  stroke: #22c55e;
  stroke-width: 12;
  opacity: 0.5;
  filter: blur(10px);
  animation: tl-neon-pulse 3.2s ease-in-out infinite;
}
.tl-neon-route,
.tl-neon-leaf-vein {
  fill: none;
  stroke: #4ade80;
  stroke-width: 4;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.tl-neon-leaf {
  fill: rgba(74, 222, 128, 0.16);
  stroke: #4ade80;
  stroke-width: 3.4;
  stroke-linejoin: round;
  animation: tl-neon-flicker 4.8s infinite 0.3s;
}
.tl-neon-leaf-vein {
  stroke: #a7f3d0;
  stroke-width: 2;
  opacity: 0.88;
}
.tl-neon-pin {
  fill: #eaffff;
  stroke: #4ade80;
  stroke-width: 2;
  animation: tl-neon-flicker 4.1s infinite 0.6s;
}

/* Windshield */
.tl-front-glass {
  fill: url(#tl-winGrad);
  stroke: rgba(255,255,255,0.6);
  stroke-width: 2;
}

/* Windows — staggered glow */
.tl-window {
  fill: url(#tl-winGrad);
  stroke: rgba(255,255,255,0.55);
  stroke-width: 2;
  animation: tl-win-glow 2.6s ease-in-out infinite;
}
.tl-window:nth-of-type(2) { animation-delay: 0.22s; }
.tl-window:nth-of-type(3) { animation-delay: 0.44s; }
.tl-window:nth-of-type(4) { animation-delay: 0.66s; }

/* Tail lamps */
.tl-tail-lamp {
  fill: #ff2200;
  filter: drop-shadow(0 0 8px rgba(255,34,0,0.9)) drop-shadow(0 0 18px rgba(255,34,0,0.5));
  animation: tl-tail-blink 1.8s ease-in-out infinite;
}
.tl-tail-lamp-top {
  fill: #ff6644; opacity: 0.85;
  filter: drop-shadow(0 0 5px rgba(255,100,68,0.7));
}

/* Front lamp */
.tl-lamp--front {
  fill: #fff7c8;
  filter: drop-shadow(0 0 12px rgba(255,213,122,0.95)) drop-shadow(0 0 24px rgba(255,200,80,0.5));
  animation: tl-lamp-front 1.6s ease-in-out infinite;
}
.tl-drl {
  fill: #ffe0a0; opacity: 0.9;
  filter: drop-shadow(0 0 5px rgba(255,224,160,0.8));
}

/* Exhaust pipe */
.tl-exhaust-pipe { fill: #2a3a4a; stroke: #405060; stroke-width: 1; }

/* Puffs */
.tl-puff { fill: rgba(200,220,200,0.28); }
.tl-puff--1 { animation: tl-puff 1.3s ease-out infinite; }
.tl-puff--2 { animation: tl-puff 1.3s ease-out infinite 0.43s; }
.tl-puff--3 { animation: tl-puff 1.3s ease-out infinite 0.86s; }

/* Wheels */
.tl-wheel {
  animation: tl-wheel-spin 0.65s linear infinite;
  will-change: transform;
}
.tl-wheel--rear  { transform-origin: 282px 447px; }
.tl-wheel--front { transform-origin: 520px 447px; }
.tl-wheel-tire { fill: #0e1a26; stroke: #28384a; stroke-width: 6; }
.tl-wheel-hub  { fill: #c4d0d8; stroke: #788c96; stroke-width: 4; }
.tl-wheel-cap  { fill: #dde8ea; }
.tl-spoke      { stroke: #5a6e78; stroke-width: 3.5; stroke-linecap: round; }

/* ═══ Keyframes ═══ */
@keyframes tl-enter {
  from { opacity: 0; transform: scale(1.012); }
  to   { opacity: 1; transform: scale(1); }
}
@keyframes tl-aurora1 {
  from { transform: translate3d(0,0,0) scale(1); }
  to   { transform: translate3d(8vw,5vh,0) scale(1.08); }
}
@keyframes tl-aurora2 {
  from { transform: translate3d(0,0,0) scale(1); }
  to   { transform: translate3d(-7vw,-6vh,0) scale(1.12); }
}
@keyframes tl-pulse {
  0%,100% { color: var(--muted); border-color: rgba(240,199,119,0.22); }
  50%     { color: var(--ink);   border-color: rgba(240,199,119,0.42); }
}
@keyframes tl-led-dot {
  0%,100% { opacity: 0.18; transform: scaleY(0.65); }
  50%     { opacity: 1;    transform: scaleY(1.18);
            box-shadow: 0 0 14px var(--led-green), 0 0 28px rgba(57,255,130,0.65); }
}

@keyframes tl-star  {
  0%,100% { opacity: 0.28; transform: scale(0.8); }
  50%     { opacity: 1;    transform: scale(1.35); }
}
@keyframes tl-moon  { 0%,100% { opacity: 0.82; } 50% { opacity: 1; } }

@keyframes tl-mtn-far  { from { transform: translateX(0); } to { transform: translateX(-12px); } }
@keyframes tl-mtn-mid  { from { transform: translateX(0); } to { transform: translateX(-20px); } }
@keyframes tl-mtn-near { from { transform: translateX(0); } to { transform: translateX(-30px); } }
@keyframes tl-fog-back  { from { transform: translateX(0); } to { transform: translateX(-160px); } }
@keyframes tl-fog-front { from { transform: translateX(0); } to { transform: translateX(-240px); } }

/* Dashes: step = 140px (spacing between rects = 140px) */
@keyframes tl-road-flow {
  from { transform: translateX(0); }
  to   { transform: translateX(-140px); }
}

@keyframes tl-hl-breathe { 0%,100% { opacity: 0.6; } 50% { opacity: 1; } }

@keyframes tl-coach-bounce {
  0%,100% { transform: translateY(0)    rotate(-0.25deg); }
  50%     { transform: translateY(-6px) rotate(0.2deg); }
}
@keyframes tl-shadow {
  0%,100% { transform: scaleX(1);    opacity: 0.42; }
  50%     { transform: scaleX(0.91); opacity: 0.26; }
}

@keyframes tl-win-glow { 0%,100% { opacity: 0.82; } 50% { opacity: 1; } }

@keyframes tl-neon-flicker {
  0%, 19%, 21%, 23%, 25%, 54%, 56%, 100% {
    opacity: 1;
    fill: #eaffff;
  }
  20%, 24%, 55% {
    opacity: 0.6;
    fill: #a7f3d0;
  }
}
@keyframes tl-neon-pulse {
  0%, 100% { opacity: 0.36; stroke-width: 8; stroke: #22c55e; }
  50%      { opacity: 0.66; stroke-width: 12; stroke: #86efac; }
}

@keyframes tl-tail-blink { 0%,100% { opacity: 0.7; } 50% { opacity: 1; } }

@keyframes tl-lamp-front {
  0%,100% { opacity: 0.8; }
  50%     { opacity: 1;
            filter: drop-shadow(0 0 18px rgba(255,213,122,1)) drop-shadow(0 0 34px rgba(255,200,80,0.7)); }
}

@keyframes tl-puff {
  0%   { opacity: 0.55; transform: translate(0,0) scale(0.55); }
  60%  { opacity: 0.28; }
  100% { opacity: 0;    transform: translate(-26px,-32px) scale(1.5); }
}

@keyframes tl-wheel-spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

@media (max-width: 768px) {
  .tl-neon-text,
  .tl-neon-icon { filter: none !important; }
  .tl-neon-text {
    filter:
      drop-shadow(0 0 3px #22c55e)
      drop-shadow(0 0 9px rgba(34,197,94,0.8))
      drop-shadow(0 0 16px rgba(134,239,172,0.6)) !important;
  }
  .tl-neon-icon {
    filter:
      drop-shadow(0 0 3px #22c55e)
      drop-shadow(0 0 8px rgba(34,197,94,0.78)) !important;
  }
  .tl-neon-halo { display: none; }
  .tl-tail-lamp { filter: drop-shadow(0 0 6px rgba(255,34,0,0.8)); }
  .tl-lamp--front { filter: drop-shadow(0 0 8px rgba(255,213,122,0.9)); }
}

/* Reduced motion — consolidated here to avoid Vue scoped CSS minification bugs */
@media (prefers-reduced-motion: reduce) {
  .trek-loader {
    animation: none !important;
  }
  .trek-loader .trek-loader__aurora,
  .tl-stars circle, .tl-moon,
  .tl-mtn path, .tl-fog path,
  .tl-road-lines, .tl-headlight,
  .tl-coach, .tl-coach-shadow,
  .tl-puff, .tl-wheel,
  .tl-neon-text, .tl-neon-halo,
  .tl-neon-leaf, .tl-neon-pin {
    animation: none !important;
  }
  .trek-loader .trek-loader__subtitle { animation-duration: 4s !important; }
  .trek-loader .led-dot { animation-duration: 3s !important; }
  .tl-window { animation-duration: 5s !important; }
  .tl-tail-lamp,
  .tl-lamp--front { animation-duration: 3.5s !important; }
}
</style>
